import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Tube } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface TubeMeshProperties extends TransformableItemProperties {
  path: THREE.Vector3[];
  tubularSegments: number;
  radius: number;
  radialSegments: number;
  closed: boolean;
  materialId?: string;
}

export interface TubeMesh extends Mesh<TubeMeshProperties> {
  subtype: 'tube';
  properties: TubeMeshProperties;
}

// Create a reusable Tube component that can be used in both preview and world
const TubeComponent = forwardRef<THREE.Mesh, { properties: TubeMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { path, tubularSegments, radius, radialSegments, closed, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // Create a curve from the path points
  const curve = new THREE.CatmullRomCurve3(path);
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Tube
      ref={ref}
      args={[curve, tubularSegments, radius, radialSegments, closed]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const TubeMeshItem: TubeMesh = {
  id: '',
  name: 'Tube',
  type: 'mesh',
  subtype: 'tube',
  icon: 'Tube',
  properties: {
    path: [
      new Vector3(-2, 0, 0),
      new Vector3(-1, 1, 0),
      new Vector3(0, 0, 0),
      new Vector3(1, -1, 0),
      new Vector3(2, 0, 0)
    ],
    tubularSegments: 64,
    radius: 0.2,
    radialSegments: 8,
    closed: false,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.tubularSegments',
      type: 'number',
      label: 'Tubular Segments',
      default: 64,
      panel: 'geometry'
    },
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 0.2,
      panel: 'geometry'
    },
    {
      key: 'properties.radialSegments',
      type: 'number',
      label: 'Radial Segments',
      default: 8,
      panel: 'geometry'
    },
    {
      key: 'properties.closed',
      type: 'boolean',
      label: 'Closed',
      default: false,
      panel: 'geometry'
    }
  ],
  render: (properties: TubeMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <TubeComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 